// Type-only import: CompositeLayer is used solely as a return-type annotation
// (PickingInfo was unused). Keeping it `import type` prevents @deck.gl/core
// from being pulled into the eager runtime entry chunk (EID-63).
import type { CompositeLayer } from '@deck.gl/core/typed';
import chroma from 'chroma-js';
import rgba from 'color-rgba';
import Handlebars from 'handlebars';
import { getPresetColormap } from './colormap';
import { ModelData } from '../../schemas/world';
import { degrees_to_cardinal } from 'utils';

Handlebars.registerHelper('round', function (value, decimals) {
  return !isNaN(parseFloat(value)) ? value.toFixed(decimals) : '-';
});

Handlebars.registerHelper('multiply', function (value, scale) {
  return !isNaN(parseFloat(value)) ? scale * value : '-';
});

Handlebars.registerHelper('add', function (value, scale) {
  return !isNaN(parseFloat(value)) ? scale + value : '-';
});

Handlebars.registerHelper('degrees_to_cardinal', function (value) {
  return !isNaN(parseFloat(value)) ? degrees_to_cardinal(value) : '-';
});

// Cheap, canonical serialization for the small query objects passed to
// cachedQuery (EID-108 P9) — replaces object-hash's SHA-1 hashing, which is
// overkill for these tiny per-layer query objects. Object keys are sorted
// before serializing, matching object-hash's default `unorderedObjects: true`
// behaviour, so two logically-equal queries built with keys in a different
// order still produce the same cache key; array element order is preserved
// (object-hash's default `unorderedArrays: false`). Dates serialize via
// toJSON() (their ISO string, the same string JSON.stringify would produce),
// so two equal-valued Date instances still collide — see the "equal-valued
// Date instances" case in worldlayer-cached-query.test.ts.
const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    const keys = Object.keys(value).sort();
    return `{${keys
      .map(
        (k) =>
          `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`,
      )
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

export abstract class BaseWorldLayer {
  nodeType = 'WorldLayer';
  id: string;
  name: string;
  dataId: string | undefined;
  layerSpec: object;
  visible: boolean;
  exclusiveOf: string[];
  linked: string;
  colormap: object;
  timeSelect: object;
  levelSelect: object;
  minZoom: number;
  maxZoom: number;
  // EID-130: interpret z as height above the terrain surface (validated per
  // layerspec schema; gated on the world node's terrain option by the deckmap).
  terrainFollow: boolean;
  // How TerrainExtension positions this layer: 'offset' lifts geometry by the
  // terrain elevation at its anchor; 'drape' renders it onto the surface.
  // Subclasses override where drape is the right geometry (e.g. wmts imagery).
  terrainDrawMode: 'offset' | 'drape' = 'offset';
  _colormapper: object;
  // Query/data cache (EID-58) — see cachedQuery() below.
  _queryHash: string | null = null;
  _querySource: object | null = null;
  _queryResult: Promise<unknown> | null = null;

  constructor(spec) {
    this.id = spec.id;
    this.name = spec.name;
    this.dataId = spec.dataId;
    this.hoverInfo = spec.hoverInfo
      ? {
          ...spec.hoverInfo,
          template: Handlebars.compile(spec.hoverInfo.template),
        }
      : null;
    this.label = spec.label;
    this.panel = spec.panel;
    this.exclusiveOf = spec.exclusiveOf || [];
    this.linked = spec.linked;
    this.visible = !!spec.visible;
    this.minZoom = spec.minZoom || 0;
    // Default must be >= the immersive views' MAX_PSEUDO_ZOOM (24): a
    // first-person camera near the ground reports a pseudo-zoom above 20,
    // and a lower default would zoom-hide every default-configured layer
    // there. Map cameras clamp at maxZoom <= 20, so they can't tell the
    // difference.
    this.maxZoom = spec.maxZoom || 24;
    this.terrainFollow = !!spec.layerSpec?.terrainFollow;
    if (spec.layerSpec.colormap) {
      let colormap = spec.layerSpec.colormap;
      if (colormap && colormap.name) {
        colormap = getPresetColormap(colormap, 10);
      }
      if (typeof colormap.domain[0] == 'string') {
        if (colormap.domain.indexOf('_default') < 0)
          colormap.domain['_default'] = 'gray';
        this._colormapper = (v) => {
          const ind = colormap.domain.indexOf(v);
          const color =
            colormap.scale[
              ind >= 0 ? ind : colormap.domain.indexOf('_default')
            ];
          try {
            return chroma(color);
          } catch (e) {
            return chroma('black');
          }
        };
      } else {
        this._colormapper = chroma
          .scale(colormap.scale)
          .domain(colormap.domain);
      }
      this.colormap = colormap;
    }

    this.layerSpec = {
      ...spec.layerSpec,
      style: this.parseStyling(spec.layerSpec.style),
    };
    this.legend =
      spec.layerSpec.legend === false || spec.layerSpec.legend === null
        ? null
        : this.getLegend();
    this.timeSelect = {
      mode: 'nearest',
      tolerance: 'PT60S',
      ...spec.timeSelect,
    };
    this.levelSelect = spec.levelSelect
      ? {
          mode: 'nearest',
          ...spec.levelSelect,
        }
      : null;
    this.pickable = true;
  }

  //Returns a Deck compatible composite layer for given data, time, level and context
  getDeckLayer(
    currentData: Promise[],
    time: Date[],
    viewstate: object,
    level: number | null,
  ): CompositeLayer<any> | null {
    return null;
  }
  //Returns a dynamic legend spec for the layer
  getLegend() {
    return null;
  }
  parseStyling(style) {
    if (!style) return {};
    const parsed_style = { ...style };
    Object.entries(style).map(([key, value]) => {
      if (key.slice(0, 3) == 'get') {
        if (value.function) {
          if (value.function == 'colormapper') {
            parsed_style[key] = (o) => {
              const a = value.args[0];
              const arg =
                o.properties && a in o.properties ? o.properties[a] : o[a];
              const _rgba = this._colormapper(arg).rgba();
              _rgba[3] = _rgba[3] * 255;
              return _rgba;
            };
          } else {
            // Compile a JS-function accessor from the spec. Accept any source
            // form: a braced body ("{ return h; }"), a bare statement body
            // ("return h;"), or a plain expression ("h * 2"). Previously only
            // the leading-"{" + non-empty-args case compiled; every other form
            // silently fell through with no else branch, leaving the raw
            // { function, args } object in parsed_style as the accessor. deck.gl
            // then coerces that object to a number, yielding NaN — which for
            // getElevation collapses an extruded polygon to a flat patch while a
            // constant number works fine.
            const fnArgs = Array.isArray(value.args) ? value.args : [];
            const src = String(value.function).trim();
            // A braced or `return`-bearing source is already a function body;
            // a bare expression must be wrapped so the function returns it.
            const body = /^\{|\breturn\b/.test(src) ? src : `return (${src});`;
            const fn = Function(...fnArgs, body);
            parsed_style[key] = (o) => {
              const args = fnArgs.map((a) =>
                o.properties && a in o.properties ? o.properties[a] : o[a],
              );
              return fn(...args);
            };
          }
        } else if (value.template) {
          const tmpl = Handlebars.compile(value.template);
          parsed_style[key] = (o) => {
            const result = tmpl(o.properties || o);
            if (key.slice(-5) == 'Color') {
              const [r, g, b, a] = rgba(result);
              return [r, g, b, a * 255];
            } else {
              return result;
            }
          };
        } else if (key.slice(-5) == 'Color' && typeof value == 'string') {
          const [r, g, b, a] = rgba(parsed_style[key]);
          parsed_style[key] = [r, g, b, a * 255];
        } else {
          parsed_style[key] = value;
        }
      }
    });
    return parsed_style;
  }

  // Per-layer-instance query/data cache (EID-58).
  // Serializes the effective query and reuses the cached data promise when
  // neither the query content nor the data source identity changed, so deck's
  // reference diffing on the `data` prop skips re-filtering / WKB decode /
  // GPU attribute re-upload on every pan or registry tick.
  // - View-dependent layers (e.g. seasurface geofilter) put the view-dependent
  //   values inside `query`, so they participate in the cache key automatically.
  // - Data refreshes are caught by source identity: the data provider wraps
  //   re-fetched data in a NEW DataWrapper instance, which invalidates the
  //   cache even when the query itself is unchanged.
  cachedQuery(
    currentData: {
      query: (query: object, squeeze?: boolean) => Promise<unknown>;
    },
    query: object,
    squeeze = true,
  ): Promise<unknown> {
    const hash = stableStringify({ query, squeeze });
    if (
      this._queryResult &&
      hash === this._queryHash &&
      currentData === this._querySource
    ) {
      return this._queryResult;
    }
    this._queryHash = hash;
    this._querySource = currentData;
    this._queryResult = currentData.query(query, squeeze);
    return this._queryResult;
  }
}
